import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ComputerDesktopIcon, 
  CameraIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { ProductCard } from '../../components/marketing/ProductCard';
import { CompactProductCarousel } from '../../components/marketing/CompactProductCarousel';
import { HoverProductShowcase } from '../../components/marketing/HoverProductShowcase';

export const ProductsPage = () => {
  const products = [
    {
      name: "DICOMFiles",
      description: "Sistema avanzado de automatización para el procesamiento de documentos DICOM. Reduce el trabajo manual en un 80-90% y elimina errores humanos.",
      icon: <ComputerDesktopIcon className="h-12 w-12" />,
      features: [
        "Automatización completa del flujo DICOM",
        "Procesamiento 24/7 sin intervención humana",
        "Integración transparente con sistemas PACS",
        "Gestión inteligente de worklist DICOM",
        "Procesamiento automático de PDFs",
        "Sistema de códigos QR integrado"
      ],
      isPopular: true,
      cta: "Ver Detalles",
      linkTo: "/dicomfiles"
    },
    {
      name: "DICOMCapture",
      description: "Herramienta de captura y procesamiento de imágenes DICOM desde equipos médicos",
      icon: <CameraIcon className="h-12 w-12" />,
      features: [
        "Captura desde equipos médicos",
        "Validación automática de calidad",
        "Procesamiento en tiempo real",
        "Integración con sistemas PACS",
        "Control de flujo de trabajo",
        "Monitoreo de rendimiento"
      ],
      isPopular: false,
      cta: "Más Información",
      linkTo: "/contact"
    }
  ];

  const benefits = [
    {
      icon: <ShieldCheckIcon className="h-8 w-8" />,
      title: "Seguridad Médica",
      description: "Cumplimiento con estándares HIPAA y GDPR para protección de datos médicos"
    },
    {
      icon: <ChartBarIcon className="h-8 w-8" />,
      title: "Análisis Avanzado",
      description: "Herramientas de análisis e inteligencia artificial para diagnóstico asistido"
    },
    {
      icon: <CheckCircleIcon className="h-8 w-8" />,
      title: "Calidad Garantizada",
      description: "Validación automática y control de calidad en cada proceso"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Nuestros Productos
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Soluciones especializadas para el manejo profesional de imágenes médicas DICOM. 
            Herramientas diseñadas por expertos para centros médicos modernos.
          </p>
        </div>
      </section>

      {/* Interactive Hover Showcase */}
      <HoverProductShowcase />

      {/* Product Carousel Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Explora Más Detalles
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Descubre información adicional sobre nuestras soluciones especializadas
            </p>
          </div>
          <CompactProductCarousel />
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comparación de Productos
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Compara las características y elige la solución perfecta para tu centro médico
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {products.map((product, index) => (
              <ProductCard
                key={index}
                name={product.name}
                description={product.description}
                features={product.features}
                icon={product.icon}
                isPopular={product.isPopular}
                cta={product.cta}
                linkTo={product.linkTo}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              ¿Por qué elegir nuestras soluciones?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Nuestros productos están diseñados específicamente para profesionales médicos 
              que requieren herramientas confiables y seguras.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow duration-200">
                <div className="text-blue-600 mb-4 flex justify-center">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Integración Completa
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Nuestros productos se integran perfectamente con tu infraestructura médica existente, 
                incluyendo equipos de diagnóstico, sistemas PACS y otros software médicos.
              </p>
              <ul className="space-y-4">
                <li className="flex items-center">
                  <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Compatibilidad con equipos DICOM estándar</span>
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Integración con sistemas PACS</span>
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">API REST para desarrolladores</span>
                </li>
                <li className="flex items-center">
                  <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Soporte para múltiples formatos</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">100%</div>
                <div className="text-gray-600 mb-6">Compatibilidad DICOM</div>
                <div className="text-4xl font-bold text-green-600 mb-2">24/7</div>
                <div className="text-gray-600 mb-6">Soporte técnico</div>
                <div className="text-4xl font-bold text-purple-600 mb-2">30 días</div>
                <div className="text-gray-600">Garantía de satisfacción</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            ¿Listo para optimizar tu flujo de trabajo DICOM?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Descubre cómo DICOMFiles y DICOMCapture pueden transformar 
            el manejo de imágenes médicas en tu centro.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="border border-white text-white px-8 py-3 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors duration-200"
            >
              Solicitar Demo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
